/**
 * Drop-in replacement for a native `<select>`: same props, same `<option>` / `<optgroup>`
 * children, same `onChange(e)` with `e.target.value` — so prototype pages port by renaming the tag.
 * Renders a button plus a searchable popover list (Radix Popover for positioning and focus).
 * The app never uses the browser's native dropdown.
 */
import { Popover } from "radix-ui";
import {
  Children,
  type CSSProperties,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";

interface Opt {
  value: string;
  label: string;
  group?: string;
  disabled?: boolean;
}

/** Event-like object so `onChange={(e) => set(e.target.value)}` keeps working. */
export interface SelectChange {
  target: { value: string; name?: string };
  currentTarget: { value: string; name?: string };
}

export interface SelectProps {
  value?: string | number | null;
  defaultValue?: string | number;
  onChange?: (e: SelectChange) => void;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  title?: string;
  placeholder?: string;
  "aria-label"?: string;
}

function text(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(text).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return text(node.props.children);
  return "";
}

function collect(children: ReactNode, group?: string, out: Opt[] = []): Opt[] {
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const el = child as ReactElement<{
      value?: string | number;
      label?: string;
      disabled?: boolean;
      children?: ReactNode;
    }>;
    if (el.type === "option") {
      const label = text(el.props.children);
      out.push({ value: String(el.props.value ?? label), label, group, disabled: el.props.disabled });
    } else if (el.type === "optgroup") {
      collect(el.props.children, el.props.label, out);
    } else if (el.props.children) {
      collect(el.props.children, group, out); // fragments, maps
    }
  });
  return out;
}

export function Select({
  value,
  defaultValue,
  onChange,
  children,
  className = "",
  style,
  disabled,
  name,
  id,
  title,
  placeholder,
  ...rest
}: SelectProps) {
  const opts = useMemo(() => collect(children), [children]);
  const [inner, setInner] = useState(String(defaultValue ?? opts[0]?.value ?? ""));
  const current = value === undefined ? inner : String(value ?? "");
  const selected = opts.find((o) => o.value === current);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // A modal <dialog> sits in the top layer and makes the rest of the page inert, so a list
  // portalled to <body> would render behind it and ignore clicks. Portal into the dialog instead.
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const filtered = q
    ? opts.filter((o) => `${o.label} ${o.group ?? ""}`.toLowerCase().includes(q.toLowerCase()))
    : opts;

  const choose = (o: Opt) => {
    if (o.disabled) return;
    if (value === undefined) setInner(o.value);
    const t = { value: o.value, name };
    onChange?.({ target: t, currentTarget: t });
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = filtered[active];
      if (o) choose(o);
    }
  };
  const openList = (o: boolean) => {
    if (o) setContainer(triggerRef.current?.closest("dialog") ?? null);
    setOpen(o);
    setQ("");
    setActive(
      Math.max(
        0,
        opts.findIndex((x) => x.value === current),
      ),
    );
  };

  let lastGroup: string | undefined;
  return (
    <Popover.Root open={open} onOpenChange={openList}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          title={title}
          aria-label={rest["aria-label"]}
          aria-haspopup="listbox"
          className={`select-btn ${className}`}
          style={style}
          onKeyDown={(e) => {
            if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
              e.preventDefault();
              openList(true);
            }
          }}
        >
          <span className={selected ? "" : "muted"}>{selected?.label ?? placeholder ?? "Select…"}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal container={container}>
        <Popover.Content
          className="select-pop"
          align="start"
          sideOffset={4}
          onKeyDown={onKey}
          onOpenAutoFocus={(e) => {
            if (opts.length <= 8) {
              e.preventDefault();
              listRef.current?.focus();
            }
          }}
        >
          {opts.length > 8 && (
            <input
              className="select-search"
              value={q}
              placeholder="Search…"
              onChange={(e) => {
                const next = e.target.value;
                setQ(next);
                const shown = opts.filter((o) =>
                  `${o.label} ${o.group ?? ""}`.toLowerCase().includes(next.toLowerCase()),
                );
                setActive(
                  Math.max(
                    0,
                    shown.findIndex((o) => !o.disabled),
                  ),
                );
              }}
            />
          )}
          <div className="select-list" role="listbox" tabIndex={-1} ref={listRef}>
            {filtered.length === 0 && <div className="select-empty">No matches</div>}
            {filtered.map((o, i) => {
              const head =
                o.group && o.group !== lastGroup ? <div className="select-group">{o.group}</div> : null;
              lastGroup = o.group;
              return (
                <div key={`${o.group ?? ""}:${o.value}`}>
                  {head}
                  {/* biome-ignore lint/a11y/useFocusableInteractive: arrow keys and Enter on the popover drive the options */}
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled by the popover's onKeyDown */}
                  <div
                    role="option"
                    aria-selected={o.value === current}
                    aria-disabled={o.disabled}
                    className={`select-opt ${i === active ? "active" : ""} ${o.value === current ? "sel" : ""}`}
                    onMouseMove={() => i !== active && setActive(i)}
                    onClick={() => choose(o)}
                  >
                    <span>{o.label || " "}</span>
                    {o.value === current && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                      >
                        <path d="M5 12.5 10 17 19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
